/**
 * The addresses of the Space era (sharing spec §5), mapped to where they now live. Pure, so the router and its tests
 * share one rule. `null` means the address is already current and stays.
 *
 * - `/workplace/<uuid>` (the old card address in bookmarks and old notifications) → `/workplace?item=<uuid>`;
 * - `/workplace/<slug>/` and `/workplace?space=…` → `/workplace`, without `space`;
 * - `/projects/<slug>/<id>/<rest>` → `/projects/<id>/<rest>`, and `/projects/<slug>` → `/projects`.
 *
 * Every other query parameter (`item`, `prj`, a view, a filter) and the hash are kept.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A project or card id, as opposed to a Space's slug. */
export const isUuid = (segment: string): boolean => UUID.test(segment);

/** The path's segments after `prefix`, empty ones dropped (a trailing slash names nothing). */
function segmentsAfter(pathname: string, prefix: string): string[] {
  return pathname.slice(prefix.length).split('/').filter((s) => s !== '');
}

function withQuery(path: string, params: URLSearchParams, hash: string): string {
  const query = params.toString();
  return `${path}${query === '' ? '' : `?${query}`}${hash}`;
}

export function legacyWorkplaceTarget(pathname: string, search: string, hash: string): string | null {
  const params = new URLSearchParams(search);
  const [first] = segmentsAfter(pathname, '/workplace');
  if (first !== undefined && isUuid(first)) {
    params.delete('space');
    params.set('item', first);
    return withQuery('/workplace', params, hash);
  }
  if (first === undefined && !params.has('space')) return null;
  params.delete('space');
  return withQuery('/workplace', params, hash);
}

export function legacyProjectTarget(pathname: string, search: string, hash: string): string | null {
  const [first, id, ...rest] = segmentsAfter(pathname, '/projects');
  if (first === undefined || isUuid(first)) return null;
  const params = new URLSearchParams(search);
  const path = id === undefined ? '/projects' : ['/projects', id, ...rest].join('/');
  return withQuery(path, params, hash);
}
